import React, { Suspense, lazy, useEffect, useState } from 'react';
import { siteData } from '../data/mock';
import { ArrowDown } from 'lucide-react';

const Spline = lazy(() => import('@splinetool/react-spline'));

export const HeroSection = () => {
  const { hero } = siteData;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const scrollToThesis = () => {
    const el = document.querySelector('#thesis');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="top"
      className="oa-grid-bg relative min-h-screen flex items-center"
      style={{ background: 'var(--bg-primary)', paddingTop: '80px' }}
    >
      <div
        className="relative z-10 w-full flex flex-col lg:flex-row items-center"
        style={{ padding: '60px 7.6923%', gap: '40px' }}
      >
        {/* Left Content */}
        <div className="flex-1 flex flex-col justify-center" style={{ maxWidth: '720px' }}>
          {/* Eyebrow */}
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ marginBottom: '32px' }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--brand-primary)',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {hero.eyebrow}
            </span>
          </div>

          {/* Headlines */}
          <div style={{ marginBottom: '32px' }}>
            {hero.headlines.map((line, i) => (
              <h1
                key={i}
                className={`oa-display-huge transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{
                  transitionDelay: `${100 + i * 120}ms`,
                  marginBottom: '4px',
                  color: i === 1 ? 'var(--brand-primary)' : 'var(--text-primary)',
                }}
              >
                {line}
              </h1>
            ))}
          </div>

          {/* Subtext */}
          <p
            className={`oa-body-medium transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '450ms', marginBottom: '56px', maxWidth: '580px' }}
          >
            {hero.subtext}
          </p>

          {/* Stat Pillars */}
          <div
            className={`flex flex-wrap gap-0 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '600ms' }}
          >
            {hero.stats.map((stat, i) => (
              <div
                key={i}
                style={{
                  padding: '16px 24px',
                  borderLeft: i === 0 ? '1px solid rgba(0, 255, 209, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                  borderLeftColor: i === 0 ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                }}
              >
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: i === 0 ? 'var(--brand-primary)' : 'var(--text-secondary)',
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: '0.5px',
                  }}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Spline 3D */}
        <div
          className="flex-1 hidden lg:flex items-center justify-center"
          style={{ minHeight: '600px', position: 'relative' }}
        >
          <div
            className={`oa-spline-wrapper transition-all duration-1000 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ transitionDelay: '500ms', width: '700px', height: '700px', overflow: 'visible', position: 'relative' }}
          >
            <Suspense
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="oa-spinner" />
                </div>
              }
            >
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </Suspense>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDelay: '800ms', zIndex: 10, cursor: 'pointer' }}
        onClick={scrollToThesis}
      >
        <span className="oa-body-muted" style={{ fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase' }}>
          Scroll
        </span>
        <ArrowDown size={16} style={{ color: 'var(--text-muted)' }} className="animate-bounce" />
      </div>
    </section>
  );
};
